import React, { useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

// Import step components
import PersonalDetails from "./profilesetup/PersonalDetails";
import FamilyInformation from "./profilesetup/FamilyInformation";
import PartnerPreferences from "./profilesetup/PartnerPreferences";
import UploadPhotos from "./profilesetup/UploadPhotos";
import UploadHoroscope from "./profilesetup/UploadHoroscope";
import UploadIdProof from "./profilesetup/UploadIdProof";
import BasicDetails from "./profilesetup/BasicDetails";
import apiEndpoints from "../apiconfig";

export default function AddUser() {
  const [step, setStep] = useState(1);
  const totalSteps = 7;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const pageNames = {
    1: "Basic Details",
    2: "Personal Details",
    3: "Family Information",
    4: "Partner Preferences",
    5: "Upload Photos",
    6: "Upload Horoscope",
    7: "Upload ID Proof",
  };

  const [showErrors, setShowErrors] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Required fields for validation
  const requiredFieldsByStep = {
    1: ["name", "email", "phone", "password"],
    2: [
      "name",
      "email",
      "age",
      "dob",
      "birth_place",
      "birth_time",
      "gender",
      "education",
      "blood_group",
      "address",
      "country",
      "state",
      "city",
      "marital_status",
      "profession",
      "annual_income",
      "religion",
      "caste",
      "zodiac_sign",
      "zodiac_star",
    ],
    3: [
      "family_type",
      "father_name",
      "mother_name",
      "father_occupation",
      "mother_occupation",
      "family_status",
    ],
    4: [
      "pref_religion",
      "pref_country",
      "pref_state",
      "pref_city",
      "pref_marital_status",
      "pref_blood_group",
      "pref_caste",
      "pref_zodiac_sign",
      "pref_zodiac_star",
      "pref_education",
      "pref_profession",
      "pref_annual_income",
    ],
    5: ["profile_photo", "profile_photo1", "profile_photo2", "profile_photo3"],
    6: ["horoscope"],
    7: ["ID_proof"],
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",

    // Personal
    name: "",
    email: "",
    age: "",
    dob: "",
    birth_place: "",
    birth_time: "",
    gender: "",
    education: "",
    blood_group: "",
    address: "",
    country: "",
    country_id: "",
    state: "",
    state_id: "",
    city: "",
    city_id: "",
    marital_status: "",
    profession: "",
    annual_income: "",
    religion: "",
    caste: "",
    zodiac_sign: "",
    zodiac_star: "",
    about_me: "",

    // Family
    family_type: "",
    father_name: "",
    mother_name: "",
    father_occupation: "",
    mother_occupation: "",
    no_of_brothers: "",
    no_of_sisters: "",
    family_status: "",
    about_family: "",

    // Partner Prefs
    pref_religion: "",
    pref_country: "",
    pref_state: "",
    pref_city: "",
    pref_marital_status: "",
    pref_blood_group: "",
    pref_caste: "",
    pref_zodiac_sign: "",
    pref_zodiac_star: "",
    pref_education: "",
    pref_profession: "",
    pref_annual_income: "",

    // Uploads
    profile_photo: null,
    profile_photo1: null,
    profile_photo2: null,
    profile_photo3: null,
    horoscope: null,
    ID_proof: null,

    // Status
    isActive: 0,
  });

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Validation and step navigation
  const handleNext = () => {
    let stepFields = requiredFieldsByStep[step] || [];

    // Skip validation for Caste, Zodiac Sign, and Star if religion is Muslim
    if (step === 2 && form.religion?.toLowerCase().includes("muslim")) {
      stepFields = stepFields.filter(
        (field) => !["caste", "zodiac_sign", "zodiac_star"].includes(field),
      );
    }

    // Skip validation for Partner Preferences (Step 4) if religion is Muslim
    if (step === 4 && form.pref_religion?.toLowerCase().includes("muslim")) {
      stepFields = stepFields.filter(
        (field) =>
          !["pref_caste", "pref_zodiac_sign", "pref_zodiac_star"].includes(
            field,
          ),
      );
    }

    let isValid = true;

    if (step === 1) {
      if (!isValid) {
        setShowErrors(true);
        showSnackbar(
          "Please fill all required fields before proceeding.",
          "error",
        ); // ✅ error toast
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        showSnackbar("Please enter a valid email address.", "error");
        return;
      }

      // Phone validation (10–12 digits)
      const phoneRegex = /^\d{10,12}$/;
      if (!phoneRegex.test(form.phone)) {
        showSnackbar("Phone number must be 10–12 digits only.", "error");
        return;
      }

      // Password validation
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{7,}$/;
      if (!passwordRegex.test(form.password)) {
        showSnackbar(
          "Password must be at least 7 characters, include one uppercase, one lowercase, and one special character.",
          "error",
        );
        return;
      }
    }

    if (step === 5) {
      // At least one profile photo required
      isValid =
        form.profile_photo?.file ||
        form.profile_photo1?.file ||
        form.profile_photo2?.file ||
        form.profile_photo3?.file;
    } else if (step === 6) {
      isValid = !!form.horoscope?.file;
    } else if (step === 7) {
      isValid = !!form.ID_proof?.file;
    } else {
      isValid = stepFields.every((field) => form[field]);
    }

    if (!isValid) {
      setShowErrors(true);
      showSnackbar(
        "Please fill all required fields before proceeding.",
        "error",
      ); // ✅ error toast
      return;
    }

    setShowErrors(false);
    showSnackbar(`${pageNames[step]} completed successfully!`, "success");
    setStep((s) => Math.min(totalSteps, s + 1));
  };

  // File uploads
  const handleMultiImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const keys = [
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
    ];

    setForm((prev) => {
      const updated = { ...prev };
      let i = 0;
      keys.forEach((key) => {
        if (!updated[key] && files[i]) {
          updated[key] = {
            file: files[i],
            name: files[i].name,
            url: URL.createObjectURL(files[i]),
          }; // ✅ store file
          i++;
        }
      });
      return updated;
    });
  };

  const handleSingleImageUpload = (e, key) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      alert("Only JPG/PNG files are allowed.");
      return;
    }

    setField(key, { file, name: file.name, url: URL.createObjectURL(file) }); // ✅ store file
  };

  const removePhotoAt = (idx) => {
    const keys = [
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
    ];
    setForm((p) => {
      const updated = { ...p };
      if (keys[idx]) updated[keys[idx]] = null;
      return updated;
    });
  };

  // Final submit
  const onSubmit = async () => {
    try {
      const formData = new FormData();

      // Append text fields (skip files)
      Object.keys(form).forEach((key) => {
        if (
          ![
            "profile_photo",
            "profile_photo1",
            "profile_photo2",
            "profile_photo3",
            "horoscope",
            "ID_proof",
          ].includes(key)
        ) {
          formData.append(
            key,
            form[key] !== undefined && form[key] !== null ? form[key] : "",
          );
        }
      });

      // Append files
      [
        "profile_photo",
        "profile_photo1",
        "profile_photo2",
        "profile_photo3",
        "horoscope",
        "ID_proof",
      ].forEach((key) => {
        if (form[key]?.file) {
          formData.append(key, form[key].file);
        }
      });

      // Send request
      const response = await fetch(
        `${apiEndpoints.userDetails}?action=insert`,
        {
          method: "POST",
          body: formData,
        },
      );

      const result = await response.json();
      if (result.status === "success") {
        showSnackbar(
          `User registered successfully: ${result.generated_id}!`,
          "success",
        );
        setTimeout(() => {
          navigate("/dashboard/approvals", { replace: true });
        }, 1500);
      } else {
        showSnackbar(`Error: ${result.message}`, "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Something went wrong. Please try again.", "error");
    }
  };

  // Render steps
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BasicDetails
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );

      case 2:
        return (
          <PersonalDetails
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 3:
        return (
          <FamilyInformation
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 4:
        return (
          <PartnerPreferences
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 5:
        return (
          <UploadPhotos
            form={form}
            handleMultiImageUpload={handleMultiImageUpload}
            removePhotoAt={removePhotoAt}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 6:
        return (
          <UploadHoroscope
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 7:
        return (
          <UploadIdProof
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: "90%", mx: "auto", p: { xs: 2, md: 3 } }}>
      <CardHeader
        title={
          <Stack spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Add User
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Step {step} of {totalSteps}
            </Typography>
          </Stack>
        }
        sx={{ pb: 0 }}
      />

      <CardContent
        sx={{
          overflowY: "auto",
          maxHeight: { xs: "calc(85vh - 160px)", md: "calc(85vh - 170px)" },
          pt: 2,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {renderStep()}
      </CardContent>

      <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
        >
          Previous
        </Button>

        {step === totalSteps ? (
          <Button
            variant="contained"
            onClick={onSubmit}
            sx={{
              backgroundColor: "#ffe599",
              color: "black",
              borderRadius: 2,
            }}
          >
            Save Profile
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            onClick={handleNext}
            sx={{
              backgroundColor: "#ffe599",
              color: "black",
            }}
          >
            Next
          </Button>
        )}
      </CardActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {
            top: "80px",
            right: "24px",
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          //variant="filled"
          sx={{
            width: "100%",
            fontFamily: "'Montserrat', sans-serif",
            alignItems: "center",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
